import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, unwrap } from "@/lib/api";
import { GlossLogomark } from "@/components/GlossLogomark";
import { useSettings } from "@/contexts/SettingsContext";
import { useBranch } from "@/contexts/BranchContext";
import { ArrowRight, ArrowLeft, Phone, Check, CalendarDays, Clock, Scissors, Sparkles, Hand, Heart, Download, Layers } from "lucide-react";
import { toast } from "sonner";
import { ConsentModal } from "@/components/ConsentModal";

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
// Module-level cache so it survives navigation within a session.
const catalogCache = { services: null, staff: null, fetchedAt: 0 };

const CATEGORIES = [
  { key: "hair",  label: "Hair",  icon: Scissors },
  { key: "skin",  label: "Skin",  icon: Sparkles },
  { key: "nails", label: "Nails", icon: Hand },
  { key: "spa",   label: "Spa",   icon: Heart },
];

function fmtICSDate(date, time) {
  // date YYYY-MM-DD · time HH:MM
  const [y, m, d] = date.split("-");
  const [h, mn] = time.split(":");
  return `${y}${m}${d}T${h}${mn}00`;
}

export default function BookPublic() {
  const { settings } = useSettings();
  const { branches } = useBranch();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    client_name: "", client_phone: "", gender: "female",
    services_requested: [], assigned_stylist_id: "", assigned_stylist_name: "",
    booking_date: "", booking_time: "",
    branch_id: "",
  });
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingConsent, setPendingConsent] = useState(null);

  useEffect(() => {
    const now = Date.now();
    if (catalogCache.services && catalogCache.staff && (now - catalogCache.fetchedAt) < CACHE_TTL) {
      setServices(catalogCache.services);
      setStaff(catalogCache.staff);
      return;
    }
    Promise.all([
      api.get("/services"),
      api.get("/staff", { params: { branch_id: "branch-001" }}),
    ]).then(([sr, st]) => {
      const services = unwrap(sr) || [];
      const staff = (unwrap(st) || []).filter((s) => ["stylist", "therapist", "nail_tech"].includes(s.role));
      catalogCache.services = services;
      catalogCache.staff = staff;
      catalogCache.fetchedAt = Date.now();
      setServices(services);
      setStaff(staff);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (branches.length && !form.branch_id) setForm((f) => ({ ...f, branch_id: branches[0].id }));
  }, [branches]); // eslint-disable-line

  const totalAmount = useMemo(() => {
    const map = Object.fromEntries(services.map((s) => [s.name, s.base_price]));
    return form.services_requested.reduce((sum, n) => sum + (map[n] || 0), 0);
  }, [form.services_requested, services]);

  const totalDuration = useMemo(() => {
    const map = Object.fromEntries(services.map((s) => [s.name, s.duration_minutes]));
    return form.services_requested.reduce((sum, n) => sum + (map[n] || 0), 0);
  }, [form.services_requested, services]);

  // Generate next 8 days for date picker
  const days = useMemo(() => {
    return Array.from({ length: 8 }).map((_, i) => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, []);

  // Fetch slots when date changes
  useEffect(() => {
    if (!form.booking_date) return;
    const params = { date: form.booking_date, branch_id: form.branch_id || "branch-001" };
    if (form.assigned_stylist_id) params.stylist_id = form.assigned_stylist_id;
    api.get("/appointments/availability", { params }).then((r) => setSlots(unwrap(r)?.slots || [])).catch(() => setSlots([]));
  }, [form.booking_date, form.assigned_stylist_id, form.branch_id]);

  const toggleService = (name) => {
    setForm((f) => ({
      ...f,
      services_requested: f.services_requested.includes(name) ? f.services_requested.filter((x) => x !== name) : [...f.services_requested, name],
    }));
  };

  const confirmBooking = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const stylist = staff.find((s) => s.id === form.assigned_stylist_id);
      const r = await api.post("/appointments/book", {
        ...form,
        client_phone: form.client_phone.startsWith("+91") ? form.client_phone : `+91 ${form.client_phone}`,
        assigned_stylist_name: stylist?.name || null,
      });
      const data = unwrap(r);
      if (!data?.token_number) throw new Error("No token returned");

      // For NEW clients (no consent on file), gate the success screen behind a consent modal
      let client = null;
      if (data.client_id) {
        try { client = unwrap(await api.get(`/clients/${data.client_id}`)); } catch {}
      }
      const needsConsent = client && !client.consent_form_signed;
      if (needsConsent) {
        setPendingConsent({ booking: data, client_id: data.client_id });
      } else {
        setConfirmation(data);
        setStep(4);
        toast.success("Booking confirmed ✨", { description: `${data.token_number} · WhatsApp confirmation sent` });
      }
    } catch (e) {
      const err = e?.response?.data?.error;
      toast.error(err?.message || "Couldn't book that slot", { description: err?.code || "Please try a different time" });
    } finally { setSubmitting(false); }
  };

  const submitConsent = async (payload) => {
    if (!pendingConsent) return;
    try {
      await api.post(`/clients/${pendingConsent.client_id}/consent`, payload);
      setConfirmation(pendingConsent.booking);
      setStep(4);
      setPendingConsent(null);
      toast.success("Consent saved ✨ Booking confirmed", { description: `${pendingConsent.booking.token_number} · WhatsApp sent` });
    } catch (e) {
      const err = e?.response?.data?.error;
      toast.error(err?.message || "Could not save consent");
    }
  };

  const reset = () => {
    setStep(1);
    setForm({
      client_name: "", client_phone: "", gender: "female",
      services_requested: [], assigned_stylist_id: "", assigned_stylist_name: "",
      booking_date: "", booking_time: "", branch_id: branches[0]?.id || "",
    });
    setConfirmation(null);
  };

  const downloadIcs = () => {
    if (!confirmation) return;
    const startStr = fmtICSDate(confirmation.booked_at.slice(0, 10), confirmation.booked_at.slice(11, 16));
    // Compute end roughly = start + totalDuration minutes
    const startDt = new Date(confirmation.booked_at);
    const endDt = new Date(startDt.getTime() + (totalDuration || 60) * 60000);
    const endStr = `${endDt.getUTCFullYear()}${String(endDt.getUTCMonth()+1).padStart(2,"0")}${String(endDt.getUTCDate()).padStart(2,"0")}T${String(endDt.getUTCHours()).padStart(2,"0")}${String(endDt.getUTCMinutes()).padStart(2,"0")}00`;
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//GLOSS//Salon OS//EN",
      "BEGIN:VEVENT",
      `UID:${confirmation.id}@gloss`,
      `DTSTAMP:${startStr}`,
      `DTSTART:${startStr}`,
      `DTEND:${endStr}`,
      `SUMMARY:${settings.salon_name} · ${confirmation.services_requested.join(" + ")}`,
      `DESCRIPTION:Token ${confirmation.token_number} · Stylist ${confirmation.assigned_stylist_name || "any"}`,
      `LOCATION:${settings.salon_name}, ${settings.branch_name}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `gloss-booking-${confirmation.token_number}.ics`; a.click();
    URL.revokeObjectURL(url);
  };

  const canStep2 = form.client_name.trim().length > 1 && form.client_phone.replace(/\D/g, "").length >= 10;
  const canStep3 = form.services_requested.length > 0;
  const canConfirm = form.booking_date && form.booking_time;

  return (
    <div className="min-h-screen w-full" data-testid="book-public">
      {/* Top brand bar */}
      <header className="px-5 sm:px-8 py-5 flex items-center gap-3">
        <button onClick={() => navigate("/")} className="btn-3d btn-glass-3d btn-sm" data-testid="book-back-btn">
          <ArrowLeft size={14} />
        </button>
        <GlossLogomark size={36} animate={false} />
        <div>
          <div className="font-display font-bold text-[18px] text-[var(--color-text-primary)] tracking-[-0.02em]">GLOSS</div>
          <div className="font-inter text-[11px] text-[var(--color-text-muted)] -mt-0.5">Book your appointment · {settings.salon_name}</div>
        </div>
      </header>

      <main className="px-5 sm:px-8 pb-14 flex justify-center">
        <div className="w-full max-w-[520px]">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-7" data-testid="book-progress">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="h-1.5 rounded-full transition-all" style={{
                width: n === step ? 28 : 10,
                background: n <= step ? "linear-gradient(90deg,#E8C98A,#B5892E)" : "rgba(124,92,122,0.15)",
              }} />
            ))}
          </div>

          <div className="glass-elevated rounded-[24px] p-7 anim-slide-up-lg" data-testid={`book-step-${step}`}>
            {step === 1 && (
              <Step1
                form={form} setForm={setForm}
                onNext={() => setStep(2)}
                disabled={!canStep2}
              />
            )}
            {step === 2 && (
              <Step2
                services={services} staff={staff}
                form={form} setForm={setForm} toggleService={toggleService}
                totalAmount={totalAmount} totalDuration={totalDuration}
                onBack={() => setStep(1)} onNext={() => setStep(3)}
                disabled={!canStep3} currency={settings.currency || "₹"}
              />
            )}
            {step === 3 && (
              <Step3
                days={days} slots={slots} form={form} setForm={setForm}
                onBack={() => setStep(2)}
                onConfirm={confirmBooking}
                submitting={submitting} disabled={!canConfirm}
              />
            )}
            {step === 4 && confirmation && (
              <Step4
                confirmation={confirmation}
                onDownloadIcs={downloadIcs}
                onReset={reset}
                services={form.services_requested}
                currency={settings.currency || "₹"}
                total={totalAmount}
              />
            )}
          </div>

          <div className="mt-6 text-center font-inter text-[11.5px] text-[var(--color-text-muted)]">
            {settings.salon_name} · {settings.branch_name} · You'll receive a WhatsApp confirmation
          </div>
        </div>
      </main>
      {pendingConsent && (
        <ConsentModal
          clientName={form.client_name}
          onClose={() => setPendingConsent(null)}
          onConfirm={submitConsent}
        />
      )}
    </div>
  );
}

function Step1({ form, setForm, onNext, disabled }) {
  return (
    <>
      <div className="overline mb-1">Step 1 of 4 · Contact</div>
      <h1 className="font-display font-bold text-[24px] tracking-[-0.02em]">Tell us about you</h1>
      <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">We'll send your booking confirmation here.</p>

      <label className="block mt-5">
        <div className="overline mb-1.5">Full name</div>
        <input
          autoFocus value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
          placeholder="Priya Sharma"
          className="w-full h-12 px-4 rounded-2xl bg-white/70 border border-white/85 font-inter text-[15px] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition"
          data-testid="book-name-input"
        />
      </label>

      <label className="block mt-3">
        <div className="overline mb-1.5">Phone</div>
        <div className="flex items-center h-12 rounded-2xl bg-white/70 border border-white/85 focus-within:border-[var(--color-gold-mid)] focus-within:ring-2 focus-within:ring-[var(--color-gold-mid)]/20 transition">
          <div className="flex items-center gap-2 pl-4 pr-3 border-r border-white/85">
            <Phone size={14} className="text-[var(--color-text-muted)]" />
            <span className="font-mono text-[15px] text-[var(--color-text-secondary)]">+91</span>
          </div>
          <input
            value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
            placeholder="98765 43210"
            className="flex-1 px-4 bg-transparent font-mono text-[15px] outline-none"
            data-testid="book-phone-input"
          />
        </div>
      </label>

      <div className="mt-4">
        <div className="overline mb-2">Gender</div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { v: "female", l: "Female" },
            { v: "male",   l: "Male" },
            { v: "other",  l: "Prefer not to say" },
          ].map((g) => (
            <button key={g.v} type="button" onClick={() => setForm({ ...form, gender: g.v })}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-inter font-medium border transition ${form.gender === g.v ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/85 text-[var(--color-text-secondary)]"}`}>
              {g.l}
            </button>
          ))}
        </div>
      </div>

      <button onClick={onNext} disabled={disabled} className="btn-3d btn-gold-3d btn-lg w-full mt-6" data-testid="book-step1-next">
        Next <ArrowRight size={16} />
      </button>
    </>
  );
}

function Step2({ services, staff, form, setForm, toggleService, totalAmount, totalDuration, onBack, onNext, disabled, currency }) {
  const [activeCat, setActiveCat] = useState("hair");
  const filtered = services.filter((s) => s.category === activeCat);
  return (
    <>
      <div className="overline mb-1">Step 2 of 4 · Service</div>
      <h1 className="font-display font-bold text-[24px] tracking-[-0.02em]">What would you like?</h1>
      <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">Pick one or more. Custom requests can be added at check-in.</p>

      {/* Category tabs */}
      <div className="flex gap-1.5 mt-5 overflow-x-auto no-scrollbar">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const isActive = c.key === activeCat;
          return (
            <button key={c.key} onClick={() => setActiveCat(c.key)}
                    className={`shrink-0 px-3 py-2 rounded-full text-[12.5px] font-sora font-semibold border transition flex items-center gap-1.5 ${isActive ? "bg-[var(--color-gold-mid)]/15 border-[var(--color-gold-mid)]/45 text-[var(--color-gold-deeper)]" : "bg-white/65 border-white/85 text-[var(--color-text-secondary)]"}`}>
              <Icon size={12} />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Services list */}
      <div className="mt-4 space-y-2 max-h-[260px] overflow-y-auto pr-1">
        {filtered.map((s) => {
          const sel = form.services_requested.includes(s.name);
          return (
            <button key={s.id} type="button" onClick={() => toggleService(s.name)}
                    className={`w-full text-left rounded-xl p-3 border transition flex items-center justify-between gap-3 ${sel ? "bg-[var(--color-gold-mid)]/12 border-[var(--color-gold-mid)]/40" : "bg-white/70 border-white/85 hover:bg-white/90"}`}
                    data-testid={`book-service-${s.id}`}>
              <div className="min-w-0">
                <div className="font-sora font-semibold text-[14px] flex items-center gap-2">{s.name} {sel && <Check size={13} className="text-[var(--color-gold-deeper)]" />}</div>
                <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">{s.duration_minutes}m · {s.subcategory}</div>
              </div>
              <div className="font-mono text-[13.5px] text-[var(--color-gold-deeper)]">{currency}{s.base_price}</div>
            </button>
          );
        })}
        {filtered.length === 0 && <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-4 text-center">No services in this category.</div>}
      </div>

      {/* Stylist */}
      <label className="block mt-5">
        <div className="overline mb-1.5">Preferred stylist</div>
        <select value={form.assigned_stylist_id}
                onChange={(e) => {
                  const s = staff.find((x) => x.id === e.target.value);
                  setForm({ ...form, assigned_stylist_id: e.target.value, assigned_stylist_name: s?.name || "" });
                }}
                data-testid="book-stylist-select"
                className="w-full h-11 px-3 rounded-xl bg-white/75 border border-white/85 font-inter text-[13.5px] outline-none">
          <option value="">No preference</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{`${s.name} · ${s.role.replace("_", " ")}`}</option>)}
        </select>
      </label>

      {/* Total bar */}
      <div className="mt-5 px-4 py-3 rounded-2xl flex items-center justify-between" style={{ background: "linear-gradient(180deg,#FFF7E4,#F5E4E4)", border: "1px solid rgba(212,168,83,0.30)" }}>
        <div className="font-inter text-[12.5px] text-[var(--color-text-secondary)]">{form.services_requested.length} service{form.services_requested.length === 1 ? "" : "s"} · {totalDuration}m</div>
        <div className="font-mono leading-none text-[var(--color-gold-deeper)]" style={{ fontSize: 24 }} data-testid="book-running-total">{currency}{totalAmount}</div>
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={onBack} className="btn-3d btn-glass-3d flex-1"><ArrowLeft size={14} /> Back</button>
        <button onClick={onNext} disabled={disabled} className="btn-3d btn-gold-3d flex-[2]" data-testid="book-step2-next">Next <ArrowRight size={14} /></button>
      </div>
    </>
  );
}

function Step3({ days, slots, form, setForm, onBack, onConfirm, submitting, disabled }) {
  return (
    <>
      <div className="overline mb-1">Step 3 of 4 · When</div>
      <h1 className="font-display font-bold text-[24px] tracking-[-0.02em]">Pick a slot</h1>
      <p className="font-inter text-[13px] text-[var(--color-text-muted)] mt-1">We'll hold this for you for 15 minutes after confirmation.</p>

      {/* Date pills */}
      <div className="mt-5 flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {days.map((d) => {
          const iso = d.toISOString().slice(0, 10);
          const isActive = form.booking_date === iso;
          return (
            <button key={iso} onClick={() => setForm({ ...form, booking_date: iso, booking_time: "" })}
                    data-testid={`book-date-${iso}`}
                    className={`shrink-0 flex flex-col items-center px-3 py-2 rounded-2xl border transition min-w-[64px] ${isActive ? "bg-[var(--color-gold-mid)]/12 border-[var(--color-gold-mid)]/40" : "bg-white/70 border-white/85"}`}>
              <span className="font-sora text-[10.5px] uppercase tracking-[0.15em] text-[var(--color-text-muted)]">{d.toLocaleDateString([], { weekday: "short" })}</span>
              <span className="font-mono text-[18px] mt-0.5" style={{ color: isActive ? "#8E6A1A" : "#1C1917" }}>{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="mt-5">
        <div className="overline mb-2 flex items-center gap-1"><Clock size={11} /> Time</div>
        {!form.booking_date ? (
          <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-4 text-center">Pick a date first.</div>
        ) : slots.length === 0 ? (
          <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-4 text-center">No slots available.</div>
        ) : (
          <div className="grid grid-cols-4 gap-1.5 max-h-[200px] overflow-y-auto pr-1">
            {slots.map((s) => {
              const isActive = form.booking_time === s.time;
              return (
                <button key={s.time}
                        onClick={() => s.available && setForm({ ...form, booking_time: s.time })}
                        disabled={!s.available}
                        data-testid={`book-slot-${s.time}`}
                        className={`px-2 py-2 rounded-lg text-[12.5px] font-mono border transition ${isActive ? "bg-[var(--color-gold-mid)]/15 border-[var(--color-gold-mid)]/45 text-[var(--color-gold-deeper)] font-medium" : s.available ? "bg-white/70 border-white/85 text-[var(--color-text-primary)] hover:bg-white" : "bg-white/30 border-white/40 text-[var(--color-text-faint)] line-through cursor-not-allowed"}`}>
                  {s.time}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={onBack} className="btn-3d btn-glass-3d flex-1"><ArrowLeft size={14} /> Back</button>
        <button onClick={onConfirm} disabled={disabled || submitting} className="btn-3d btn-gold-3d flex-[2]" data-testid="book-confirm-btn">
          {submitting ? "Confirming…" : "Confirm booking"} {!submitting && <Check size={14} />}
        </button>
      </div>
    </>
  );
}

function Step4({ confirmation, onDownloadIcs, onReset, services, currency, total }) {
  const dt = new Date(confirmation.booked_at);
  return (
    <div className="text-center">
      <div className="overline mb-3">Booking confirmed</div>
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(180deg,rgba(212,168,83,0.20),rgba(212,168,83,0.05))", border: "1px solid rgba(212,168,83,0.35)" }}>
          <Check size={28} className="text-[var(--color-gold-deeper)]" />
        </div>
      </div>
      <div className="font-mono leading-none text-[var(--color-gold-deeper)] mb-3" style={{ fontSize: 64, letterSpacing: "-0.02em" }} data-testid="book-token">
        {confirmation.token_number}
      </div>
      <h2 className="font-display font-bold text-[22px] tracking-[-0.02em]">You're all set ✨</h2>
      <p className="font-inter text-[14px] text-[var(--color-text-secondary)] mt-2">
        {services.join(" + ")}
        <br />
        {dt.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} at {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        {confirmation.assigned_stylist_name && <><br />with {confirmation.assigned_stylist_name}</>}
      </p>
      <div className="mt-4 inline-block px-4 py-2 rounded-2xl glass-subtle">
        <span className="font-inter text-[12.5px] text-[var(--color-text-muted)]">Estimated bill · </span>
        <span className="font-mono text-[14px] text-[var(--color-gold-deeper)]">{currency}{total}</span>
      </div>

      <div className="mt-6 flex flex-col gap-2.5">
        <button onClick={onDownloadIcs} className="btn-3d btn-glass-3d btn-lg w-full" data-testid="book-ics-btn">
          <CalendarDays size={16} /> Add to calendar (.ics)
        </button>
        <button onClick={onReset} className="font-sora font-semibold text-[13px] text-[var(--color-mauve-deep)] hover:underline mt-2" data-testid="book-another-btn">
          Book another →
        </button>
      </div>
    </div>
  );
}
